import React from 'react';

import { connect } from 'react-redux';
import BaseComponent from '../../../baseClasses/BaseComponent';
import dataModel from '../../../models/DataModel';
import MultipleUsersIcon from '../../../components/Iconoclast/MultipleUsersIcon';
import UserAvatarWithInfoPopout from '../UserAvatarWithInfoPopout';
import MultipleUserPopup from '../../../components/MultipleUserPopup/index';
import overflowSafePopupPosition from '../../../helpers/popupPositioning';
import '../UserAvatar.scss';
import './UserAvatarWithInfo.scss';

// Matches .avatar-info__box in UserAvatarWithInfoPopout.scss.
const POPOUT_WIDTH = 256;
const POPOUT_HEIGHT = 201;

export class UserAvatarWithInfo extends BaseComponent {
  constructor() {
    super();

    this.assignmentTitles = {
      'field_user': this.textMap.assignedTo,
      'reviewer': this.textMap.reviewer,
      'approver': this.textMap.approver,
    };
  }

  componentDidMount() {
    // Fixed coordinates are absolute to the viewport, so they go stale when the page moves
    // under an open popup. Recompute rather than leaving it detached from its avatar.
    this.handleRepositionPopup = () => {
      if (this.props.overflowSafePopup && this.props.assignment == this.props.displayed) {
        this.setState({ popupPosition: this.calculatePopupPosition() });
      }
    };
    window.addEventListener('resize', this.handleRepositionPopup);
    window.addEventListener('scroll', this.handleRepositionPopup, true);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleRepositionPopup);
    window.removeEventListener('scroll', this.handleRepositionPopup, true);
  }

  calculatePopupPosition() {
    // Measure the visible avatar circle rather than the .avatar-info wrapper: the wrapper
    // carries a negative left margin and is wider than the circle, so centring on it lands
    // the popup a few pixels to the right of the icon.
    const wrapper = this.wrapperRef;
    const circle = wrapper && wrapper.querySelector ? wrapper.querySelector('.user-avatar') : null;
    const height = this.props.multiAssignedUsers ? null : POPOUT_HEIGHT;
    return overflowSafePopupPosition(circle || wrapper, POPOUT_WIDTH, height);
  }

  handleShowInfo(e) {
    e.stopPropagation();
    if (this.props.overflowSafePopup) {
      this.setState({ popupPosition: this.calculatePopupPosition() });
    }
    this.props.handleInfoOpen(this.props.assignment);
  }

  handleClick(e) {
    e.stopPropagation();
    if (this.props.overflowSafePopup) {
      this.setState({ popupPosition: this.calculatePopupPosition() });
    }
    if (this.props.handleSelected) {
      this.props.handleSelected(this.props.user);
    }
  }

  render() {
    let isDisplayed = this.props.assignment == this.props.displayed;
    let {
      user,
      selected,
      large,
      larger,
      largest,
      extraLarge,
      small,
      title,
      company,
      multiAssigned,
      assignment,
      multiAssignedUsers,
    } = this.props;
    const selectedClass = selected ? ' selected' : '';
    let disableClass = '';
    if (!this.props.handleSelected) {
      disableClass = ' disabled';
    }
    let profileImage = user ? (user.prefetchedImage && dataModel.isOffline ? user.prefetchedImage : user.profile_image) : null;
    
    let userImage = null;
    let imageSize = '';
    if (small) {
      imageSize = ' small';
    }
    else if (large) {
      imageSize = ' large';
    }
    else if (larger) {
      imageSize = ' larger';
    }
    else if (largest) {
      imageSize = ' largest';
    }
    else if (extraLarge) {
      imageSize = ' extra-large';
    }
    if (profileImage) {
      userImage = (
        <div
          id={this.props.id}
          className={
            'user-avatar has-info' +
            selectedClass + imageSize + disableClass
          }
          onClick={this.handleShowInfo}
        >
          <div className="user-image-container">
            <img
              className="profile-image"
              alt="User"
              src={profileImage}
              data-id={user.primary_company_id}
            />
          </div>
        </div>
      );
    }
    else if (multiAssigned) {
      userImage = (
      <div 
        id={this.props.id} 
        className={'user-avatar' + selectedClass + (larger ? ' larger' : '') + (large ? ' large' : small ? ' small' : '') + (extraLarge ? ' extra-large' : '') + (largest ? ' largest' : '') + disableClass} 
        onMouseDown={this.handleMouseDown} 
        onClick={this.handleClick}
      >
        {this.state.menu}
        <div className="user-image-container" data-id='multiple-users-icon'>
          <MultipleUsersIcon />
        </div>
      </div>
      );
    }
    else if (user) {
      const initials =
        (user.first_name ? user.first_name.substr(0, 1).toUpperCase() : '') +
        (user.last_name ? user.last_name.substr(0, 1).toUpperCase() : '');
      userImage = (
        <div
          id={this.props.id}
          className={
            'user-avatar has-info' +
            selectedClass + imageSize + disableClass
          }
          onClick={this.handleShowInfo}
        >
          <div className="default-user-image-container" data-id={user.id}>
            <div className="default-user-image" data-id={user.id} />
            <div className="user-initials" data-id={user.id}>
              {initials}
            </div>
          </div>
        </div>
      );
    } else {
      const initials = 'NA';
      if (!this.user_id) {
        this.user_id = Math.floor(Math.random() & 9999);
      }
      userImage = (
        <div
          id={this.props.id}
          className={
            'user-avatar has-info not-assigned' +
            selectedClass + imageSize + disableClass
          }
        >
          <div className="default-user-image-container" data-id={this.user_id}>
            <div className="default-user-image" data-id={this.user_id} />
            <div className="user-initials" data-id={this.user_id}>
              {initials}
            </div>
          </div>
        </div>
      );
    }
    // With overflowSafePopup the popup is positioned off the trigger's rect so it can use
    // `position: fixed` and escape the clipping scroll container it renders inside.
    const measured = this.props.overflowSafePopup ? this.state.popupPosition : null;
    const popupTopPosition = measured ? measured.top : this.props.popupTopPosition;
    const popupLeftPosition = measured ? measured.left : this.props.popupLeftPosition;

    let infoPopout;
    let companyName;
    if (user) {
      if (company) {
        companyName = company.name;
      }
      if (user.companies && user.companies.length > 0) {
        companyName = user.companies[0].name;
      }
      infoPopout = (
        <UserAvatarWithInfoPopout
          handleCloseInfo={this.props.handleInfoClose}
          userImage={userImage}
          user={user}
          companyName={companyName}
          title={title}
          popupTopPosition={popupTopPosition}
          popupLeftPosition={popupLeftPosition}
          flipped={measured ? measured.flipped : false}
        />
      );
    }

    if (multiAssignedUsers) {
      infoPopout = (
        <MultipleUserPopup
          isMobile={false}
          title={this.assignmentTitles[assignment]}
          users={multiAssignedUsers}
          popupTopPosition={popupTopPosition}
          popupLeftPosition={popupLeftPosition}
          handleCloseClicked={this.props.handleInfoClose}
        />
      );
    }
    return (
      <div className="avatar-info" ref={el => { this.wrapperRef = el; }}>
        {userImage}
        {isDisplayed && infoPopout}
      </div>
    );
  }
}

// Change to appropriate class name

const mapStateToProps = (state, ownProps) => {
  return {
    permissions: state.new_users.permissions,
  };
};

export default connect(mapStateToProps)(UserAvatarWithInfo);



// WEBPACK FOOTER //
// ./src/components/UserAvatar/UserAvatarWithInfo/index.js